import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    Typography,
    Box,
    Paper,
    Chip,
    Switch,
    Divider
} from '@mui/material';
import {
    Palette as PaletteIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon
} from '@mui/icons-material';
import { useThemeContext } from '../../context/ThemeContext';

/**
 * Theme Settings Dialog Component
 * Allows users to switch between light/dark mode and different dark theme variants
 */
const ThemeSettings = ({ open, onClose }) => {
    const { mode, darkTheme, availableDarkThemes, toggleColorMode, setDarkTheme } = useThemeContext();

    const handleDarkThemeChange = (event) => {
        setDarkTheme(event.target.value);
    };

    const getThemePreview = (themeKey, themeConfig) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    mt: 0.5
                }}
            >
                <Box
                    sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: themeConfig.colors.background.default,
                        border: '1px solid rgba(0,0,0,0.2)'
                    }}
                />
                <Box
                    sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: themeConfig.colors.background.paper,
                        border: '1px solid rgba(0,0,0,0.2)'
                    }}
                />
            </Box>
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: { minHeight: 400 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaletteIcon />
                Theme Settings
            </DialogTitle>
            
            <DialogContent>
                {/* Light/Dark Mode Toggle */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Display Mode
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {mode === 'light' ? <LightModeIcon /> : <DarkModeIcon />}
                                <Typography>
                                    {mode === 'light' ? 'Light Mode' : 'Dark Mode'}
                                </Typography>
                            </Box>
                            <Switch
                                checked={mode === 'dark'}
                                onChange={toggleColorMode}
                                inputProps={{ 'aria-label': 'Toggle dark mode' }}
                            />
                        </Box>
                    </Paper>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Dark Theme Variants */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Dark Theme Variants
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose your preferred dark theme style.
                    </Typography>
                    
                    <FormControl component="fieldset" fullWidth disabled={mode === 'light'}>
                        <RadioGroup
                            value={darkTheme}
                            onChange={handleDarkThemeChange}
                            sx={{ gap: 1 }}
                        >
                            {Object.entries(availableDarkThemes).map(([key, config]) => (
                                <Paper 
                                    key={key} 
                                    sx={{ 
                                        p: 2, 
                                        backgroundColor: mode === 'light' ? 'action.disabledBackground' : 'action.hover',
                                        opacity: mode === 'light' ? 0.6 : 1
                                    }}
                                >
                                    <FormControlLabel
                                        value={key}
                                        control={<Radio />}
                                        label={
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography variant="subtitle1">
                                                        {config.name}
                                                    </Typography>
                                                </Box>
                                                {getThemePreview(key, config)}
                                            </Box>
                                        }
                                        sx={{ 
                                            margin: 0,
                                            width: '100%',
                                            '& .MuiFormControlLabel-label': {
                                                width: '100%'
                                            }
                                        }}
                                    />
                                </Paper>
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>

                {mode === 'light' && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Switch to dark mode to preview and select different dark theme variants.
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ThemeSettings;